<script lang="ts">
const FETCH_RETRY_DELAYS = [1000, 2 * 1000, 4 * 1000, 4 * 1000];
</script>

<script setup lang="ts">
import MarketplaceCancelBtn from '@/modules/marketplace/components/MarketplaceCancelBtn.vue';
import MarketplaceOrderHistory from '@/modules/marketplace/components/MarketplaceOrderHistory.vue';
import MarketplaceOrderSummary from '@/modules/marketplace/components/MarketplaceOrderSummary.vue';
import MarketplaceOrderDetailsCardChildOrders from '@/modules/marketplace/components/MarketplaceOrderDetailsCardChildOrders.vue';
import MarketplaceToggleRouteBtn from '@/modules/marketplace/components/MarketplaceToggleRouteBtn.vue';
import { OmsHistoryResponse, OmsOrder } from '@/modules/marketplace/models';
import { store as vuex } from '@/store/store';
import { computed, onMounted, shallowRef, watch } from 'vue';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import wait from '@/modules/common/services/wait';

const props = withDefaults(
  defineProps<{
    orderRef: string;
    hideButtons?: boolean;
    asBroker?: boolean;
    shouldRetryFetch?: boolean;
  }>(),
  { hideButtons: false, asBroker: false, showRetryFetch: false }
);

const emit = defineEmits<{
  (e: 'order-loaded' | 'edit-order', order: OmsOrder): void;
  (e: 'open-loan-details', orderRef: string | null): void;
}>();

watch(
  () => props.orderRef,
  () => {
    retryCount.value = 0;
    fetchStatus.value = 'fetching';
    void fetch();
  }
);

const socketEvents = computed(() => vuex.state.socketEvents);
watch(
  () => socketEvents.value.marketplace.orders,
  (payload: { orderRef: string } | null) => {
    if (payload?.orderRef === props.orderRef) void fetch();
  }
);

const hasTraderUserRole = computed(() => vuex.getters.hasTraderUserRole);

const marketplaceOrderDetails = shallowRef<OmsHistoryResponse | null>(null);

const fetchStatus = shallowRef<'fetching' | 'completed' | 'failed'>('fetching');
const fetchError = shallowRef('');

const tabIndex = shallowRef(0);
const retryCount = shallowRef(0);
const fetchRetryDelays = shallowRef(FETCH_RETRY_DELAYS);
const abortController = shallowRef<AbortController | null>(null);

const order = computed(() => marketplaceOrderDetails.value?.order);

const marketplaceApiService = useMarketplaceApiService();

async function fetch(): Promise<void> {
  if (abortController.value) {
    // we want to initiate a new request, but the previous one is still pending
    // cancel to avoid stale responses arriving late (and potentially in the wrong order)
    abortController.value.abort();
  }
  abortController.value = new AbortController();
  try {
    const res = props.asBroker
      ? await marketplaceApiService.fetchAdminOrderDetails(
          props.orderRef,
          abortController.value.signal
        )
      : await marketplaceApiService.fetchOrderDetails(props.orderRef, abortController.value.signal);
    if (!res) {
      // request was cancelled, there's another response arriving
      return;
    }

    marketplaceOrderDetails.value = res;
    fetchStatus.value = 'completed';

    emit('order-loaded', res.order);
  } catch (e) {
    if (props.shouldRetryFetch) {
      await retryFetch(e);
    } else {
      fetchStatus.value = 'failed';
      fetchError.value = e as string;
    }
  }
}

async function retryFetch(lastError: unknown): Promise<void> {
  if (retryCount.value >= fetchRetryDelays.value.length) {
    fetchStatus.value = 'failed';
    fetchError.value = lastError as string;
  } else {
    retryCount.value += 1;
    const delay = fetchRetryDelays.value[retryCount.value - 1];
    await wait(delay);
    await fetch();
  }
}

onMounted(fetch);
</script>

<template>
  <VCard class="d-flex flex-column flex-grow-1 marketplace-order-details-card" elevation="0">
    <VRow v-if="fetchStatus === 'fetching'" class="justify-center pt-16 flex-column align-center">
      <FulfillingBouncingCircleSpinner :animation-duration="1500" color="#4caf50" :size="50" />
      <h3 class="mt-2">Fetching order details...</h3>
      <h4 v-if="retryCount > 0">(This may take a bit longer)</h4>
    </VRow>

    <VRow
      v-else-if="fetchStatus === 'failed'"
      class="justify-center pt-16 flex-column align-center"
    >
      <VAlert dense type="error">
        {{ fetchError }}
      </VAlert>
    </VRow>

    <template v-else-if="fetchStatus === 'completed' && order">
      <div class="d-flex flex-column" style="height: 100%">
        <VTabs v-model="tabIndex" class="flex-grow-1 d-flex flex-column" style="overflow: hidden">
          <VTab>History</VTab>
          <VTab>Child Orders</VTab>
          <VTab data-test-id="tab-order-details">Order Details</VTab>
          <VTabsItems v-model="tabIndex" class="flex-grow-1">
            <VTabItem style="overflow: auto !important">
              <MarketplaceOrderHistory
                :details="marketplaceOrderDetails"
                @open-loan-details="emit('open-loan-details', $event)"
              />
            </VTabItem>
            <VTabItem>
              <MarketplaceOrderDetailsCardChildOrders :parent-id="order.orderRef" />
            </VTabItem>
            <VTabItem>
              <div class="pa-4 d-flex flex-column flex-grow-1" style="gap: 1rem">
                <div class="flex-shrink-1 flex-grow-1" style="overflow-x: auto; position: relative">
                  <!-- Table with order summary -->
                  <MarketplaceOrderSummary
                    :as-broker="asBroker"
                    :order="order"
                    style="position: absolute; inset: 0; right: auto"
                  />
                </div>

                <VRow v-if="!hideButtons && !asBroker && ['OPEN', 'HELD'].includes(order.status)">
                  <!-- Action buttons -->
                  <VCol cols="12">
                    <div class="actions d-flex">
                      <MarketplaceToggleRouteBtn
                        :order-ref="order.orderRef"
                        :status="order.status"
                        v-on="$listeners"
                      />

                      <MarketplaceCancelBtn :order-ref="order.orderRef" v-on="$listeners" />

                      <AuroraBtn
                        color="secondary"
                        data-test="edit-order-btn"
                        :disabled="!hasTraderUserRole"
                        timeframe="createLoans"
                        @click="emit('edit-order', order)"
                      >
                        Edit
                      </AuroraBtn>
                    </div>
                  </VCol>
                </VRow>
              </div>
            </VTabItem>
          </VTabsItems>
        </VTabs>
      </div>
    </template>
  </VCard>
</template>

<style lang="scss">
.panel-content:has(.marketplace-order-details-card) {
  overflow-y: hidden !important;
}
</style>

<style lang="scss" scoped>
.actions {
  gap: 1rem;
}

::v-deep {
  .v-window__container {
    height: 100%;

    .v-window-item {
      flex-grow: 1;

      display: flex;
      flex-direction: column;
      justify-items: stretch;
    }
  }
}
</style>
